import modules from '@/modules';
import remoteLogger from '@/api/remoteLoggerApi';
import deviceService from '@/services/webpushService';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const ROOT = { root: true };
const TAG = 'sync';
const DEBUG = false;

function getLastSyncDate() {
  if (isNaN(Date.parse(localStorage.getItem('lastSyncDateTime')))) {
    return new Date();
  }
  return new Date(Date.parse(localStorage.getItem('lastSyncDateTime')));
}

export const SYNC_STATES = {
  NEVER: 'never',
  IN_PROGRESS: 'inProgress',
  COMPLETED: 'completed',
  FAILURE: 'failure',
  INVALIDATED: 'invalidated',
};

const state = {
  isSynchronizing: true,
  currentSync: null,
  // each module has a sync state which can be NEVER, IN_PROGRESS, COMPLETED, FAILURE and INVALIDATED.
  // INVALIDATED is used to allow a module to re-synchronize
  // sync state by MODULE ID (defined in each module). {state,msg}
  syncState: {}, //createInitialModuleState(SYNC_STATES.NEVER),

  // Summarized state, but COMPLETED only if ALL are completed, FAILURE as soon we got 1 failed
  syncStateSummary: SYNC_STATES.NEVER,
  lastSyncDateTime: getLastSyncDate(),
  syncDuration: Number(localStorage.getItem('lastSyncDuration')),
  dataSynchronizationComplete: false,
};

const getters = {
  isSyncCompleted: (state) => state.syncStateSummary === SYNC_STATES.COMPLETED,
  isSyncInFailureState: (state) => state.syncStateSummary === SYNC_STATES.FAILURE,
  isSyncInProgress: (state) => state.isSynchronizing,
};

const mutations = {
  setIsSynchronizing(state, isSynchronizing) {
    state.isSynchronizing = isSynchronizing;
  },
  setLastSyncDateTime(state, { lastSyncDate, syncDuration }) {
    state.lastSyncDateTime = lastSyncDate;
    state.syncDuration = syncDuration;
    localStorage.setItem('lastSyncDateTime', lastSyncDate.toISOString());
    localStorage.setItem('lastSyncDuration', syncDuration);
  },

  /**
   * Set the sync state by module and if all modules have been synchronized, the overall sync state is set (commit setIsSynchronizing)
   * @param {*} moduleId of the module id to set sync state
   * @param {*} syncState one of the SYNC_STATES
   * @param {*} msg message to set along the syncState
   */
  setSyncState(state, { moduleId, syncState = SYNC_STATES.INVALIDATED, msg }) {
    state.syncState[moduleId] = { state: syncState, msg: msg };
  },

  setCurrentSync(state, currentSyncText) {
    state.currentSync = currentSyncText;
  },
  setCurrentSyncStates(state, { currentSync, syncStateSummary }) {
    state.currentSync = currentSync;
    state.syncStateSummary = syncStateSummary;
  },
  setDataSynchronizationComplete(state, value) {
    state.dataSynchronizationComplete = true;
  },
};

const actions = {
  /**
   * Invalidates the sync state of all registered modules
   */
  invalidateAllModuleSyncStates({ state }) {
    Object.keys(state.syncState).forEach((moduleId) => (state.syncState[moduleId].state = SYNC_STATES.INVALIDATED));
  },

  /**
   * Invalidate the cache of the given module (moduleId)
   * @param {string} moduleId of the module id to set sync state
   * @param {boolean} syncAfterAuthentication indicates the sync happens right after the authentication
   */
  invalidateModuleCacheAndSync({ rootState, commit, dispatch }, payload) {
    let syncAfterAuthentication = payload ? payload.syncAfterAuthentication : false;
    let moduleId = payload ? payload.moduleId : null;
    console.log('invalidateModuleCacheAndSync for module ', moduleId);
    if (moduleId) {
      commit('setSyncState', { moduleId, syncState: SYNC_STATES.INVALIDATED });
    }
    dispatch('synchronizeApp', { syncAfterAuthentication });
  },

  /**
   * Forces the synchronization of the app
   * Set syncAfterAuthentication to true to indicate this is a synchronization process immediately after authentication.
   */
  forceSynchronizeApp({ rootState, commit, dispatch }, payload) {
    let syncAfterAuthentication = payload ? payload.syncAfterAuthentication : false;
    commit('setIsSynchronizing', false);
    dispatch('synchronizeAppIfNotSynching', { syncAfterAuthentication });
  },

  /**
   * Start a synchronization process if not synchronizing already
   */
  synchronizeAppIfNotSynching({ rootState, state, commit, dispatch }, payload) {
    let syncAfterAuthentication = payload ? payload.syncAfterAuthentication : false;

    if (!state.isSynchronizing) {
      dispatch('invalidateAllModuleSyncStates');
      dispatch('synchronizeApp', { syncAfterAuthentication });
    }
  },
  /**
   * Synchronize app.
   * All modules which are not in sync state COMPLETED are synchronized, then the sendbox is sent.
   * @param {*} syncAfterAuthentication true to indicate files need caching (usually in pwa mode)
   */
  async synchronizeApp({ state, rootState, rootGetters, commit, dispatch }, payload) {
    let syncAfterAuthentication = payload ? payload.syncAfterAuthentication : false;

    let startTime = Date.now();

    if (state.isSynchronizing) {
      console.log('Request to synchronize app has been cancelled because a synchronization is in process already');
      return Promise.resolve();
    }
    if (!rootGetters['lifecycle/isReady']) {
      console.log('Request to synchronize app has been cancelled because app is not in lifecycle <isReady>');
      return;
    }
    remoteLogger.log(TAG, 'Sync started');
    console.time('sync-boostrap');

    remoteLogger.log(TAG, 'Start with renewing of webpush subscription (in pwa mode only)');
    if (rootState.isServerReachable) {
      console.log('Update web push registration');
      if (!('Notification' in window)) {
        console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
        console.log('This browser does not support desktop notification');
        console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
      } else {
        console.log('subscribe device for web push notifications (working as pwa only)');
        console.log('Notification.permission = ' + Notification.permission);

        // Works in pwa as in non-pwa mode
        // If the permission is not granted yet, a dialog will be displayed
        // TODO: Re-register only in case we have no valid registration
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            console.log('Notification permission granted');
            if (navigator.serviceWorker) {
              navigator.serviceWorker.getRegistration().then((reg) => {
                deviceService
                  .registerDeviceForWebPush(reg, rootState.configs.publicVapidKey)
                  .then((response) => {
                    console.debug('registerDeviceForWebPush success', JSON.stringify(response, null, 2));
                  })
                  .catch((err) => {
                    console.warn('registerDeviceForWebPush failed. WebPush Functionality not working. Error', err.message);
                  });
              });
            }
          } else {
            console.log('Notification permission denied');
            remoteLogger.cockpitLog({
              message: `Notification permission denied`,
              messageType: 'notification_permission_denied',
            });
          }
        });
      }
    }
    const connection = window.navigator.connection;
    let effectiveNetworkType;
    if (connection) {
      effectiveNetworkType = connection.effectiveType; //A string that is either 'slow-2g', '2g', '3g', or '4g'.
    }
    await remoteLogger.cockpitLog({
      message: `Sync started with network quality ${effectiveNetworkType}`,
      messageType: 'sync_start',
    });
    if (!rootState.isServerReachable) {
      console.log('Sync running (but not displayed) because server is not reachable');
    }
    commit('setIsSynchronizing', true);
    dispatch('lifecycle/stopIdleWatcher', {}, ROOT);

    console.timeEnd('sync-boostrap');

    console.log(' - Load configurations');
    await dispatch('configs/loadConfigurations', {}, ROOT);

    try {
      if (navigator.onLine) {
        // make sure the token is up-to-date and contains the most current groups the user belongs to
        await axios.post('/users/renew-tokens');
      }
    } catch (err) {
      console.warn('Failed to renew-token', err);
    }

    try {
      let times = await remoteLogger.getNow();
      let dbTime = times.dbTime ? format(parseISO(times.dbTime), 'dd.MM.yyyy HH:mm:ss') : '';
      let apiTime = times.apiTime ? format(parseISO(times.apiTime), 'dd.MM.yyyy HH:mm:ss') : '';
      let localTime = format(new Date(), 'dd.MM.yyyy HH:mm:ss');

      console.log('+---------------------------------+');
      console.log(`| db time:    ${dbTime} |`);
      console.log(`| api time:   ${apiTime} |`);
      console.log(`| local time: ${localTime} |`);
      console.log('+---------------------------------+');
    } catch (err) {
      console.warn('could not fetch the db time', err.message);
    }
    // Need to await the sendbox is stored to be sure we receive sync data that reflects the sendbox items
    console.log(`-------------------------------------------------`);
    console.log(`Synchronize sendbox`);
    console.log(`.................................................`);
    console.time('synchronizeSendbox');
    await dispatch('sendbox/sendSendbox', {}, ROOT);
    console.timeEnd('synchronizeSendbox');

    let priorities = [
      'Media',
      'Employee',
      'Video',
      'welcome',
      'Messaging',
      'MeinDienst',
      'News',
      'Document',
      'Form',
      'Wagenbuch',
      'Weblink',
      'Unfalldesk',
      'ELearning',
      'Fahrdienst452',
      'Gallery',
    ];
    let currentPathParams = window.location.pathname.split('/');
    if (currentPathParams.length > 1) {
      // start with videos if you are on video module
      if (currentPathParams[1] === 'videos') {
        let ind = priorities.indexOf('Video');
        priorities.splice(ind, 1);
        priorities.unshift('Video');
      }
    }
    // Trigger module syncs of configured modules
    const sortedModules = Object.keys(rootState.configs.moduleConfigsByModuleType).sort((a, b) => {
      let indexA = priorities.indexOf(a);
      let indexB = priorities.indexOf(b);
      return indexA - indexB;
    });

    const configuredModuleTypes = ['welcome'].concat(sortedModules);
    console.log(`sync order: ${configuredModuleTypes}`);

    const MODULES_BY_MODULE_TYPE = modules.MODULES_BY_MODULE_TYPE;

    const synchronizedModules = [];

    // Trigger required module data sync
    for (let i = 0; i < configuredModuleTypes.length; i++) {
      const moduleType = configuredModuleTypes[i];
      const moduleIndex = MODULES_BY_MODULE_TYPE[moduleType];

      if (!moduleIndex && moduleType !== 'welcome') {
        console.warn('moduleIndex for ', moduleType, 'is not defined');
      }

      let moduleId = moduleIndex ? moduleIndex.moduleId : moduleType;

      //console.log(`Check if module <${moduleId}> needs data synchronisation`);
      if (needsSync(state, moduleId)) {
        let syncTries = 0;
        let syncError = false;

        while ((syncError && syncTries < 3) || syncTries == 0) {
          syncTries++;
          syncError = false;

          commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.IN_PROGRESS }, ROOT);
          console.time(`${moduleId}/synchronizeData`);
          console.log(`-------------------------------------------------`);
          console.log(`Start synchronizing module <${moduleId}> data, try ${syncTries}/3`);
          console.log(`.................................................`);

          synchronizedModules.push(moduleId);

          commit('setCurrentSync', moduleId);
          try {
            await dispatch(`${moduleId}/synchronizeData`, { syncAfterAuthentication }, { root: true });
            await commit(`${moduleId}/countModuleItems`, {}, { root: true });
            if (!rootState.isPwaMode) {
              commit('setSyncState', { moduleId, syncState: SYNC_STATES.COMPLETED });
            }
          } catch (err) {
            console.error(`Data sync of module <${moduleId}> failed`, err);
            syncError = true;
          } finally {
            console.timeEnd(`${moduleId}/synchronizeData`);
            if (!syncError) {
              console.log(`${moduleId} successfully synced`);
            }
            console.log(`---------`);
            console.log('');
          }
        }
      }
    }
    console.log('dataSynchronizationComplete', true);
    commit('setDataSynchronizationComplete', true);

    // Dispatch a method "onComplete" to notify each module that the sync has been completed
    for (let i = 0; i < configuredModuleTypes.length; i++) {
      const moduleType = configuredModuleTypes[i];
      const moduleIndex = MODULES_BY_MODULE_TYPE[moduleType];

      let moduleId = moduleIndex ? moduleIndex.moduleId : moduleType;

      try {
        await dispatch(`${moduleId}/onSyncComplete`, {}, { root: true });
        if (DEBUG) console.log(`notified module ${moduleId} that the sync process is complete`);
      } catch (err) {
        console.error(`failed to notify module ${moduleId} of the sync completion`, err);
      }
    }

    await dispatch('getAndSetAppCounter');

    // Cache files if in pwa mode and network connection is fast enough.
    // If effective tyoe cannot be detected (browser incompatibility), the isPwaMode flag is the only decisive variable
    if (rootState.isPwaMode && (!effectiveNetworkType || ['4g'].includes(effectiveNetworkType))) {
      await remoteLogger.cockpitLog({
        message: `File sync started`,
        messageType: 'file_sync_start',
      });
      for (let i = 0; i < configuredModuleTypes.length; i++) {
        const moduleType = configuredModuleTypes[i];
        const moduleIndex = MODULES_BY_MODULE_TYPE[moduleType];
        let moduleId = moduleIndex ? moduleIndex.moduleId : moduleType;
        //console.log(`Check if module <${moduleId}> needs file synchronisation`);
        const isVideoModuleAndOffline = moduleId === 'videos' && !navigator.onLine;
        if (needsSync(state, moduleId) && !isVideoModuleAndOffline) {
          //skip videosP
          if (moduleId === 'videos') continue;
          console.log(` `);
          console.log(`-------------------------------------------------`);
          console.log(`Start synchronizing module <${moduleId}> files`);
          console.log(`.................................................`);
          try {
            //console.time(`${moduleId}/synchronizeFiles`);
            commit('setCurrentSync', `${moduleId} Files`);
            await dispatch(`${moduleId}/synchronizeFiles`, {}, { root: true });
            commit('setSyncState', { moduleId, syncState: SYNC_STATES.COMPLETED });

            //console.timeEnd(`${moduleId}/synchronizeFiles`);
          } catch (err) {
            console.warn('Failed to sync files', err);
            commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
          }
        }
      }
    }

    commit('setCurrentSyncStates', { currentSync: null, syncStateSummary: SYNC_STATES.COMPLETED });
    commit('setIsSynchronizing', false);

    let time = Math.round((Date.now() - startTime) / 100) / 10;
    console.log(` `);
    console.log(`-------------------------------------------------`);
    console.log(`Modules synchronized in ${time} seconds. Synced modules: ${synchronizedModules.join(', ')}`);
    console.log(`.................................................`);
    await remoteLogger.cockpitLog({
      message: `Sync completed in ${time}sec, modules ${synchronizedModules.map((m) => m.substring(0, 3)).join(',')}`,
      messageType: 'sync_end',
    });
    if (navigator.storage && navigator.storage.estimate) {
      navigator.storage.estimate().then((values) => {
        // quota.usage -> Number of bytes used.
        // quota.quota -> Maximum number of bytes available.
        const percentageUsed = Math.round((values.usage / values.quota) * 100 * 100);
        const usage = Math.round(values.usage / 1024 / 1024 / 10.24) / 100;
        const quota = Math.round(values.quota / 1024 / 1024 / 10.24) / 100;
        console.log(`Used ${usage}GB / ${quota}GB used (${percentageUsed}%)`);
      });
    }
    console.log('report sync time if we are online');
    if (rootState.isConnected && rootState.isServerReachable) {
      commit('setLastSyncDateTime', { lastSyncDate: new Date(), syncDuration: time });
    }

    dispatch('lifecycle/startIdleWatcher', {}, ROOT);
  },

  /**
   * Count and aggregate alll module counters and stores it to the cache storage in order to be accessible by the service-worker
   * @param {*} param0
   */
  async getAndSetAppCounter({ state, rootState, rootGetters }) {
    const activeModules = rootState.configs.modules;
    let unreadCount = 0;
    activeModules.forEach((module) => {
      let moduleIndex = modules.MODULES_BY_MODULE_TYPE[module.type];
      let moduleId = moduleIndex ? moduleIndex.moduleId : '';
      if (moduleId && module.hasBadge) {
        unreadCount += rootGetters[`${moduleId}/getUnreadCountByModuleInstance`](module.instance) || 0;
      }
    });

    // finally set appUnreadCounter to the application cache (simulated artificial endpoint), which is used to be accessible in service-worker (in contrary to the local storage).
    // The counter is needed in service-worker since this is the place to receive web push notifications (even when the app is not running) and we might be able to set the badge counter
    try {
      caches
        .open('app-db')
        .then((cache) => cache.put(new Request('https://artificial-nxg-url/appUnreadCount'), new Response(unreadCount)))
        .catch((e) => console.log('failed to set app badgeCounter to application cache'));
    } catch (err) {
      console.log('cache not defined - probably not in pwa mode');
    }
    //if (navigator && navigator.setAppBadge) await navigator.setAppBadge(unreadCount); //Works only if started as PWA
    if (navigator && navigator.setAppBadge) {
      // Clear the badge
      navigator.clearAppBadge().catch((error) => {
        // Do something with the error.
      });
      navigator.setAppBadge(unreadCount).catch((error) => {
        //Works only if started as PWA
        //Do something with the error.
        console.error('setAppBadge got exception', error);
      });
    }

    console.log('getAndSetAppCounter for App Badge', unreadCount);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

function getModuleSyncState(state, moduleId) {
  let moduleSyncState = state.syncState[moduleId];
  if (!moduleSyncState) moduleSyncState = { state: SYNC_STATES.INVALIDATED };
  return moduleSyncState;
}

function needsSync(state, moduleId) {
  const moduleSyncState = getModuleSyncState(state, moduleId);
  let needsSync = moduleSyncState && moduleSyncState.state !== SYNC_STATES.COMPLETED;
  console.log('Test if module', moduleId, 'needs sync', needsSync);
  return needsSync;
}
